import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Stethoscope, ArrowLeft, TrendingUp, Award, Target } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const Analytics = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [performance, setPerformance] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAnalytics();
  }, []);

  const fetchAnalytics = async () => {
    try {
      const [perfRes, historyRes] = await Promise.all([
        axios.get('/analytics/performance'),
        axios.get('/analytics/history')
      ]);
      setPerformance(perfRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <nav className="border-b border-border bg-white dark:bg-slate-900 sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button data-testid="back-to-dashboard-button" variant="outline" onClick={() => navigate('/dashboard')} className="border-2 border-slate-300 text-slate-700 hover:bg-slate-100">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-primary" />
              <span className="text-xl font-bold text-primary font-['Manrope']">Performance Analytics</span>
            </div>
          </div>
        </div>
      </nav>

      <div className="container mx-auto px-6 py-8" data-testid="analytics-content">
        {/* Summary Cards */}
        <div className="grid md:grid-cols-3 gap-6 mb-8">
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-primary/10">
                <Target className="w-6 h-6 text-primary" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Total Scenarios</h3>
            </div>
            <p className="text-3xl font-bold text-primary font-['Manrope']" data-testid="total-scenarios">
              {performance?.scenarios_completed || 0}
            </p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-success/10">
                <Award className="w-6 h-6 text-success" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Average Score</h3>
            </div>
            <p className="text-3xl font-bold text-success font-['Manrope']" data-testid="average-score">
              {performance?.average_score?.toFixed(1) || '0.0'}%
            </p>
          </div>
          
          <div className="stat-card">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 rounded-lg bg-accent/10">
                <TrendingUp className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-sm font-medium text-muted-foreground">Practice Time</h3>
            </div>
            <p className="text-3xl font-bold text-accent font-['Manrope']" data-testid="practice-time">
              {Math.floor((performance?.total_practice_time || 0) / 60)} hrs
            </p>
          </div>
        </div>

        {/* Recent Sessions */}
        <div className="medical-card p-6">
          <h2 className="text-2xl font-semibold font-['Manrope'] mb-6">Recent Sessions</h2>
          {history.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <p>No completed sessions yet. Start a simulation to see your progress!</p>
              <Button data-testid="start-first-simulation-button" onClick={() => navigate('/dashboard')} className="mt-4 bg-[#0F766E] hover:bg-[#0d6960] text-white">
                Start Your First Simulation
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {history.map((session, index) => (
                <div key={session.id} className="border border-border rounded-lg p-4 hover:shadow-md transition-shadow" data-testid={`session-${index}`}>
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-semibold">Session {index + 1}</p>
                      <p className="text-sm text-muted-foreground">
                        {new Date(session.started_at).toLocaleDateString()}
                      </p>
                    </div>
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      session.status === 'completed' 
                        ? 'bg-success/10 text-success' 
                        : 'bg-yellow-500/10 text-yellow-600'
                    }`}>
                      {session.status}
                    </span>
                  </div>
                  <p className="text-sm mt-2">
                    Actions performed: <span className="font-medium">{session.actions?.length || 0}</span>
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Encouragement */}
        <div className="medical-card p-6 mt-8 bg-gradient-to-r from-primary/5 to-accent/5">
          <h3 className="text-lg font-semibold font-['Manrope'] mb-2">Keep Practicing!</h3>
          <p className="text-muted-foreground">
            You're on your way to mastering clinical skills. Each simulation builds your confidence and competence as a nursing professional.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Analytics;